import Box from '@mui/material/Box';

import { Styles } from '@t2c/shared-fe';
import { CreateActionCard } from '../../Common/card';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useBrollCaption, useDialog } from '../../hooks';
import Stack from '@mui/material/Stack';
import { CreateBrollCaptionDialog } from './CreateBrollCaptionDialog';
import { CircularProgress, Typography, Grid, Button } from '@mui/material';
import {
  BrollRenderStatus,
  MediaStatus,
  ContentType,
  EMPTY_PAGE_TEXT,
  PAGE_DATA_LIMIT,
  RECENT_UPLOADS_LIMIT,
} from '../../../t2c-fe/utils';
import EmptyPage from '../../../t2c-fe/Common/EmptyPage';
import { useRouter } from 'next/router';
import { Iconify, NextImage, useHistory } from '@sdf/base';
import { FetchBrollCaptionParams } from '../../../t2c-fe/services/broll-caption/fetchBrollCaptionData';
import { FiltersAndSorting } from '../../../t2c-fe/Common/FiltersAndSorting';
import { BrollCaptionCard } from './card/BrollCaptionCard';
import { CreationBtnCard } from '../../../t2c-fe/Common/CreationBtnCard';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSnackbar } from 'notistack';

export const BrollCaptionPage = () => {
  const [params, setParams] = useState<FetchBrollCaptionParams>({
    sortOrder: 'desc',
    limit: PAGE_DATA_LIMIT,
    sortBy: 'updatedAt',
  });

  const {
    isCreateBrollCaptionDialogOpen,
    openCreateBrollCaptionDialog,
    closeCreateBrollCaptionDialog,
    brollCaptions,
    count,
    isBrollCaptionsLoading,
    refetchDataBrollCaptions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBrollCaption(params);

  const { query } = useRouter();

  const { previousRoute } = useHistory();
  useEffect(() => {
    if (
      query.action === 'create' &&
      (previousRoute === '/dashboard' || previousRoute?.startsWith('/broll-caption/'))
    )
      openCreateBrollCaptionDialog();
  }, [query]);

  const { push } = useRouter();
  const [previewVideoUrl, setPreviewVideoUrl] = useState();
  const { enqueueSnackbar } = useSnackbar();

  const handleVideoClick = (id: string, status: MediaStatus, renderStatus: BrollRenderStatus) => {
    if (status === MediaStatus.Success && renderStatus !== BrollRenderStatus.InProgress)
      push(`/edit-video/${id}`);
  };

  const handlePreview = (id) => {
    push(`/broll-caption/${id}`);
  };

  const handleButtonClick = () => {
    if ((brollCaptions?.pages?.[0]?.recentVideosCount || 0) > RECENT_UPLOADS_LIMIT) { 
      enqueueSnackbar(
        'You have reached the upload limit. For support, contact support@txt2create.com.', 
        {
          variant: 'error',
        }
      );
    } else {
      openCreateBrollCaptionDialog();
    }
  };

  const filterOptions = [
    {
      id: 1,
      name: 'Draft',
      value: BrollRenderStatus.InDraft,
    },
    {
      id: 2,
      name: 'Rendering',
      value: [BrollRenderStatus.InProgress, BrollRenderStatus.Error].toString(),
    },
    {
      id: 3,
      name: 'Rendered',
      value: BrollRenderStatus.Success,
    },
    {
      id: 4,
      name: 'Failed',
      value: BrollRenderStatus.Error,
    },
  ];

  const handleFilterOptionChange = (val: BrollRenderStatus) => {
    if (val === BrollRenderStatus.Error) {
      setParams({
        ...params,
        filters: { status: val },
      });
      return;
    }
    if (val === BrollRenderStatus.InDraft) {
      setParams({
        ...params,
        filters: {
          renderStatus: val,
          status: [MediaStatus.NotStarted, MediaStatus.InProgress, MediaStatus.Success].toString(),
        },
      });
      return;
    }

    setParams({ ...params, filters: { renderStatus: val } });
  };
  return (
    <Stack
      id="scrollableDiv"
      rowGap={3}
      pb={2}
      sx={{
        backgroundColor: (theme) => theme.palette.background.primary,
      }}
    >
      <Box sx={styles.cardContainer}>
        <CreationBtnCard
          title="Captions & Effects"
          subText="Add captions and effects to the video"
          buttonText="Create Captions & Effects"
          onButtonClick={handleButtonClick}
          isButtonDisabled={false}
          icon="BrollCaptionWhiteIcon.svg"
        />
      </Box>

      {/* Title and Sorting Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Videos</Typography>
        <Box>
          <FiltersAndSorting
            params={params}
            setParams={setParams}
            filterArray={filterOptions}
            handleFilterOptionChange={handleFilterOptionChange}
          />
        </Box>
      </Box>

      {/* Render Dialog and Video Status Accordions */}
      {isCreateBrollCaptionDialogOpen && (
        <CreateBrollCaptionDialog
          isOpen={isCreateBrollCaptionDialogOpen}
          onCloseDialog={closeCreateBrollCaptionDialog}
          refetchData={refetchDataBrollCaptions}
        />
      )}

      {isBrollCaptionsLoading ? (
        <Stack sx={styles.loader}>
          <CircularProgress />
        </Stack>
      ) : !(brollCaptions?.pages?.[0]?.brollCaptionVideos ?? []).length ? (
        <EmptyPage text={EMPTY_PAGE_TEXT.video} type={ContentType.Video} />
      ) : (
        <Box>
          <InfiniteScroll
            dataLength={
              brollCaptions?.pages?.reduce(
                (acc, page) => acc + page.brollCaptionVideos.length,
                0
              ) || 0
            }
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={
              <Box p={2} textAlign={'center'}>
                <h2>loading...</h2>
              </Box>
            }
            // endMessage={
            //   <p style={{ textAlign: 'center' }}>
            //     <b>Yay! You have seen it all</b>
            //   </p>
            // }
          >
            <Grid container spacing={4}>
              {brollCaptions.pages.map((page, pageIndex) => (
                <Fragment key={pageIndex}>
                  {page.brollCaptionVideos.map((res) => (
                    <Grid item flex={1} xs={12} sm={6} md={4} lg={3} key={res.id}>
                      <BrollCaptionCard
                        data={res}
                        refreshBrollData={refetchDataBrollCaptions}
                        handleOnPreview={handlePreview}
                      />
                    </Grid>
                  ))}
                </Fragment>
              ))}
            </Grid>
          </InfiniteScroll>
        </Box>
      )}
    </Stack>
  );
};

const styles: Styles = {
  cardContainer: {
    minHeight: 180,
  },
  videoCardsContainer: { display: 'flex', flexWrap: 'wrap', gap: 2 },
  loader: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20%',
  },
  filterBox: {
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: (theme) => theme.palette.background.secondary,
    padding: 1,
    borderRadius: '6px',
    width: '123px',
    cursor: 'pointer',
  },
  menuText: { color: (theme) => theme.palette.text.primary, fontSize: '12px' },
  filtersAndSorting: {
    display: 'flex',
    gap: 2,
  },
};
